<template>
  <ProductSelection
    v-if="showProductSelectionModal"
    :default-lang="defaultLang"
    :available-products-arr="availableProducts"
    :chosen-products-arr="products"
    :product-search-str="productSearchStr"
    @close-action-modal="closeProductSelectionModal"
  />
  <CategorySelection
    v-if="showProductCategoryListSelectionModal"
    :default-lang="defaultLang"
    :available-categories-arr="availableCategories"
    :chosen-categories-arr="shopsProductsCategories"
    :category-search-str="categorySearchStr"
    @close-action-modal="closeProductCategoryListSelectionModal"
  />
  <SupplierSelection
    v-if="showManufacturerSelectionModal"
    :available-manufacturers-arr="availableManufacturers"
    :chosen-manufacturers-arr="manufacturers"
    :manufacturer-search-str="manufacturerSearchStr"
    @close-action-modal="closeManufacturerSelectionModal"
  />

  <div class="mb-4 alert-info">
    {{ t('storeParameters.services.productRecommendations.title') }}
  </div>

  <div class="selection-cards">
    <!-- Products -->
    <div class="selection-card" @click="showProductSelectionModal = true">
      <div class="card-icon">
        <i class="fa-thin fa-box" />
      </div>
      <div class="card-content">
        <span class="card-label">{{ t('storeParameters.services.productRecommendations.excludeProductsLabel') }}</span>
        <span class="card-action">{{ t('storeParameters.services.productRecommendations.excludeProductsBtn') }}</span>
      </div>
      <div class="card-badge" :class="{ 'has-items': products && products.length > 0 }">
        <span class="badge-count">{{ products?.length || 0 }}</span>
      </div>
      <i class="fa-solid fa-chevron-right card-arrow" />
    </div>

    <!-- Categories -->
    <div class="selection-card" @click="showProductCategoryListSelectionModal = true">
      <div class="card-icon">
        <i class="fa-thin fa-folder-tree" />
      </div>
      <div class="card-content">
        <span class="card-label">{{ t('storeParameters.services.productRecommendations.excludeCategoriesLabel') }}</span>
        <span class="card-action">{{ t('storeParameters.services.productRecommendations.excludeCategoriesBtn') }}</span>
      </div>
      <div class="card-badge" :class="{ 'has-items': shopsProductsCategories.length > 0 }">
        <span class="badge-count">{{ shopsProductsCategories.length }}</span>
      </div>
      <i class="fa-solid fa-chevron-right card-arrow" />
    </div>

    <!-- Manufacturers -->
    <div class="selection-card" @click="showManufacturerSelectionModal = true">
      <div class="card-icon">
        <i class="fa-thin fa-industry" />
      </div>
      <div class="card-content">
        <span class="card-label">{{ t('storeParameters.services.productRecommendations.excludeSupplierLabel') }}</span>
        <span class="card-action">{{ t('storeParameters.services.productRecommendations.excludeSupplierBtn') }}</span>
      </div>
      <div class="card-badge" :class="{ 'has-items': manufacturers.length > 0 }">
        <span class="badge-count">{{ manufacturers.length }}</span>
      </div>
      <i class="fa-solid fa-chevron-right card-arrow" />
    </div>
  </div>

  <div class="mt-4">
    <SpmButton
      :label="t('save')"
      :loading="saveLoading"
      class="p-button p-button-primary w-full"
      @click="saveRecommendationExclusion"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProductSelection from '@/views/shop/tabs/modals/ProductSelection.vue';
import CategorySelection from '@/views/shop/tabs/modals/CategorySelection.vue';
import SupplierSelection from '@/views/shop/tabs/modals/SupplierSelection.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  Maybe, ShopsManufacturers, ShopsProducts, ShopsProductsCategoriesList,
} from '@/types/generated-types/graphql';
import {
  GetProductSellingsList,
  saveExclusions,
} from '@/composables/shop/ShopsConfiguration';
import { showToastSuccess } from '@/helpers';
import { unserialize } from 'php-serialize';
import { loadShopsLang, UserState } from '@/composables/User';

interface Exclusions {
  shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude: {};
  shopRequiredDataCrosselling_product_picker_crosselling_exclude: {};
  shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude: {};
}

interface SelectedItem {
  combinations?: string[];
  id: string;
  name: string;
  spmId: number;
  image?: string;
}

interface SelectedRecord {
  operator: string;
  selected: SelectedItem[];
}

interface Selection {
  selection: string;
}

export default defineComponent({
  name: 'ProductRecommendationServiceParameter',

  components: {
    SpmButton,
    ProductSelection,
    CategorySelection,
    SupplierSelection,
  },

  props: {
    shopId: {
      type: Number,
      required: true,
    },
  },

  emits: ['close'],

  setup(props) {
    const { t } = useI18n();
    const defaultLang = ref('');
    const saveLoading = ref(false);

    const showProductSelectionModal = ref(false);
    const showProductCategoryListSelectionModal = ref(false);
    const showManufacturerSelectionModal = ref(false);
    const shopsProductsCategories = ref<ShopsProductsCategoriesList[]>([]);
    const availableCategories = ref<ShopsProductsCategoriesList[]>([]);
    const categorySearchStr = ref('');
    const manufacturers = ref<ShopsManufacturers[]>([]);
    const availableManufacturers = ref<ShopsManufacturers[]>([]);
    const manufacturerSearchStr = ref('');
    const products = ref<ShopsProducts[]>([]);
    const availableProducts = ref<ShopsProducts[]>([]);
    const productSearchStr = ref('');

    const closeProductSelectionModal = (list: ShopsProducts[], productsAvailable: ShopsProducts[], searchStr: string) => {
      showProductSelectionModal.value = false;
      products.value = list;
      availableProducts.value = productsAvailable;
      productSearchStr.value = searchStr;
    };

    const closeProductCategoryListSelectionModal = (list: ShopsProductsCategoriesList[], categoriesAvailable: ShopsProductsCategoriesList[], searchStr: string) => {
      showProductCategoryListSelectionModal.value = false;
      shopsProductsCategories.value = list;
      availableCategories.value = categoriesAvailable;
      categorySearchStr.value = searchStr;
    };

    const closeManufacturerSelectionModal = (list: ShopsManufacturers[], manufacturersAvailable: ShopsManufacturers[], searchStr: string) => {
      showManufacturerSelectionModal.value = false;
      manufacturers.value = list;
      availableManufacturers.value = manufacturersAvailable;
      manufacturerSearchStr.value = searchStr;
    };

    const saveRecommendationExclusion = async () => {
      saveLoading.value = true;
      const exclusionObject: Exclusions = {
        shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude: {},
        shopRequiredDataCrosselling_product_picker_crosselling_exclude: {},
        shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude: {},
      };
      if (products.value && products.value.length > 0) {
        const selectedProducts: { id: number; name: string; image: Maybe<string> | undefined; spmId: number; combinations: {} }[] = [];
        products.value.forEach((product) => {
          selectedProducts.push({
            id: product.id_product,
            name: product.name,
            image: product.image_link,
            spmId: product.id_shop_product,
            combinations: {},
          });
        });

        exclusionObject.shopRequiredDataCrosselling_product_picker_crosselling_exclude = {
          selection: JSON.stringify({
            operator: 'or',
            selected: selectedProducts,
          }),
        };
      }

      if (shopsProductsCategories.value && shopsProductsCategories.value.length > 0) {
        const selectedProductsCategories: { id: number; spmId: number; name: string; combinations: {} }[] = [];
        shopsProductsCategories.value.forEach((category) => {
          selectedProductsCategories.push({
            id: category.id_category,
            name: category.name,
            spmId: category.id_shop_product_category_list,
            combinations: {},
          });
        });

        exclusionObject.shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude = {
          selection: JSON.stringify({
            operator: 'or',
            selected: selectedProductsCategories,
          }),
        };
      }

      if (manufacturers.value && manufacturers.value.length > 0) {
        const selectedManufacturers: { id: string; spmId: number; name: string; combinations: {} }[] = [];
        manufacturers.value.forEach((manufacturer) => {
          selectedManufacturers.push({
            id: manufacturer.id_manufacturer,
            name: manufacturer.name,
            spmId: manufacturer.id_shop_manufacturer,
            combinations: {},
          });
        });

        exclusionObject.shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude = {
          selection: JSON.stringify({
            operator: 'or',
            selected: selectedManufacturers,
          }),
        };
      }

      const result = await saveExclusions(exclusionObject, props.shopId);
      saveLoading.value = false;
      if (result) {
        await showToastSuccess('savedSuccessful');
      }
    };

    onMounted(async () => {
      // Charger la langue par défaut
      await loadShopsLang(props.shopId);
      const shopsLangs = UserState.activeShop?.langs ?? [];
      defaultLang.value = shopsLangs.find((lang) => lang.default)?.id ?? shopsLangs[0]?.id ?? '';

      let unserializedRecord: Selection = {
        selection: JSON.stringify({
          operator: 'or',
          selected: [
            {
              id: '',
              name: '',
              spmId: 0,
              image: '',
            },
          ],
        }),
      };
      const key = 'shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude,'
        + 'shopRequiredDataCrosselling_product_picker_crosselling_exclude,'
        + 'shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude';
      const resultData = await GetProductSellingsList(props.shopId, key, 'key, value');

      (resultData ?? []).forEach((record) => {
        unserializedRecord = unserialize(record.value);
        if (unserializedRecord.selection && unserializedRecord.selection !== '') {
          const jsonRecord: SelectedRecord = JSON.parse(unserializedRecord.selection);
          if (record.key === 'shopRequiredDataCrosselling_product_picker_categories_crosselling_exclude') {
            (jsonRecord.selected ?? []).forEach((selectedRecord: SelectedItem) => {
              const productCategories = {
                id_shop_product_category_list: selectedRecord.spmId,
                id_shop: props.shopId,
                id_category: Number(selectedRecord.id),
                lang: '',
                name: selectedRecord.name,
                link: '',
                active: '1',
                date_creation: '',
                date_modification: '',
                date: '',
              };
              shopsProductsCategories.value.push(productCategories);
            });
          } else if (record.key === 'shopRequiredDataCrosselling_product_picker_crosselling_exclude') {
            (jsonRecord.selected ?? []).forEach((selectedRecord: SelectedItem) => {
              const productRecord = {
                id_shop_product: selectedRecord.spmId,
                id_shop: props.shopId,
                id_product: Number(selectedRecord.id),
                lang: '',
                name: selectedRecord.name,
                link: '',
                image_link: selectedRecord.image ?? '',
                currency: '',
                price: 0,
                quantity: 0,
                active: true,
                date_creation: '',
                date_modification: '',
                date: '',
              };
              products.value.push(productRecord);
            });
          } else if (record.key === 'shopRequiredDataCrosselling_product_picker_manufacturers_crosselling_exclude') {
            (jsonRecord.selected ?? []).forEach((selectedRecord: SelectedItem) => {
              const manufacturer = {
                id_shop_manufacturer: selectedRecord.spmId,
                id_shop: props.shopId,
                id_manufacturer: selectedRecord.id,
                name: selectedRecord.name,
                active: '1',
                date_creation: '',
                date_modification: '',
                date: '',
              };
              manufacturers.value.push(manufacturer);
            });
          }
        }
      });
    });

    return {
      t,
      defaultLang,
      saveLoading,
      showProductSelectionModal,
      closeProductSelectionModal,
      showProductCategoryListSelectionModal,
      closeProductCategoryListSelectionModal,
      showManufacturerSelectionModal,
      closeManufacturerSelectionModal,
      products,
      shopsProductsCategories,
      manufacturers,
      saveRecommendationExclusion,
      availableProducts,
      productSearchStr,
      availableCategories,
      categorySearchStr,
      availableManufacturers,
      manufacturerSearchStr,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.alert-info {
  color: #666;
  background-color: #f6f6f6;
  padding: 1rem 1.25rem;
  border-radius: .5rem;
  border: none;
  font-size: 0.9rem;
  line-height: 1.5;
}

.selection-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.selection-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: $white;
  border: 1px solid $solitude;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $brand-color-primary;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    .card-arrow {
      color: $brand-color-primary;
      transform: translateX(3px);
    }
  }

  .card-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 8px;
    flex-shrink: 0;

    i {
      font-size: 1.5rem;
      color: $brand-color-primary;
    }
  }

  .card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    .card-label {
      font-size: 0.75rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .card-action {
      font-size: 0.95rem;
      color: $tuna;
      font-weight: 500;
    }
  }

  .card-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 0.5rem;
    background: $solitude;
    border-radius: 16px;
    flex-shrink: 0;

    .badge-count {
      font-size: 0.875rem;
      font-weight: 600;
      color: $mid-grey;
    }

    &.has-items {
      background: $brand-color-primary;

      .badge-count {
        color: $white;
      }
    }
  }

  .card-arrow {
    color: $mid-grey;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
}
</style>
